import React, { useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    useCreateOption,
    useCreateQuestion,
    useCreateQuestionnaire,
    useDeleteOption,
    useDeleteQuestion,
    useDeleteQuestionnaire,
    useQuestionnaires,
    useQuestionnaireResults,
    useUpdateOption,
    useUpdateQuestion,
    useUpdateQuestionnaire,
} from '../../hooks/questionnaires';
import type {
    CreateOptionDto,
    CreateQuestionDto,
    CreateQuestionnaireDto,
    Questionnaire,
    QuestionnaireOption,
    QuestionnaireQuestion,
    QuestionnaireResultAnswer,
    QuestionnaireStatus,
    QuestionType,
} from '../../types/questionnaires';
import { t } from '../../i18n/t';

const QUESTION_TYPES: QuestionType[] = [
    'single_choice',
    'multi_choice',
    'boolean',
    'number',
    'text',
    'scale',
    'date',
];

const statusLabel = (status: string) => {
    const key = `questionnaires.status.${status}`;
    const label = t(key);
    return label === key ? status : label;
};

const formatAnswer = (answer: QuestionnaireResultAnswer) => {
    const title = answer.questionTitle || `#${answer.questionId}`;
    let value = '';
    if (answer.optionLabels.length) value = answer.optionLabels.join(', ');
    else if (answer.valueNumber != null) value = String(answer.valueNumber);
    else if (answer.valueText != null) value = answer.valueText;
    else if (answer.valueBool != null) value = answer.valueBool ? t('questionnaires.yes') : t('questionnaires.no');
    else if (answer.valueDate != null) value = answer.valueDate;
    return `${title}: ${value}`;
};

const DashboardQuestionnaires = () => {
    const { data: questionnaires = [], isLoading, refetch } = useQuestionnaires();
    const deleteQuestionnaire = useDeleteQuestionnaire();
    const deleteQuestion = useDeleteQuestion();
    const deleteOption = useDeleteOption();
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const { data: results = [], isLoading: resultsLoading } = useQuestionnaireResults(selectedId);
    const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [editing, setEditing] = useState<Questionnaire | null>(null);
    const [questionDialog, setQuestionDialog] = useState<QuestionnaireQuestion | 'new' | null>(null);
    const [optionDialog, setOptionDialog] = useState<QuestionnaireOption | 'new' | null>(null);

    const selected = questionnaires.find((item) => item.id === selectedId) ?? null;
    const questions = selected?.questions ?? [];
    const selectedQuestion = questions.find((item) => item.id === selectedQuestionId) ?? null;
    const options = selectedQuestion?.options ?? [];

    const deactivate = async (message: string, action: () => Promise<unknown>) => {
        if (!window.confirm(message)) return;
        try {
            await action();
        } catch (e) {
            console.error(e);
            alert(t('questionnaires.saveFailed'));
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
                {t('questionnaires.title')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button variant="contained" onClick={() => setCreateOpen(true)}>
                    {t('questionnaires.new')}
                </Button>
                <Tooltip title={t('questionnaires.refresh')}>
                    <IconButton onClick={() => refetch()}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <Section title={t('questionnaires.questionnaires')}>
                <SimpleTable
                    empty={t('questionnaires.empty')}
                    columns={[t('questionnaires.colCode'), t('questionnaires.colTitle'), t('questionnaires.colOrder'), t('questionnaires.colStatus')]}
                    rows={questionnaires.map((item) => ({
                        id: item.id,
                        selected: item.id === selectedId,
                        cells: [item.code, item.title, item.order, <StatusChip key="s" status={item.status} />],
                        onSelect: () => {
                            setSelectedId(item.id);
                            setSelectedQuestionId(null);
                        },
                        onEdit: () => setEditing(item),
                        onDelete: () =>
                            deactivate(t('questionnaires.deactivateConfirm', { title: item.title }), () =>
                                deleteQuestionnaire.mutateAsync(item.id),
                            ),
                    }))}
                />
            </Section>

            <Section
                title={t('questionnaires.questions')}
                action={
                    selected ? (
                        <Button size="small" onClick={() => setQuestionDialog('new')}>
                            {t('questionnaires.newQuestion')}
                        </Button>
                    ) : null
                }
            >
                {!selected ? (
                    <Typography color="text.secondary">{t('questionnaires.selectQuestionnaire')}</Typography>
                ) : (
                    <SimpleTable
                        empty={t('questionnaires.emptyQuestions')}
                        columns={[t('questionnaires.colCode'), t('questionnaires.colTitle'), t('questionnaires.colType'), t('questionnaires.colStatus')]}
                        rows={questions.map((item) => ({
                            id: item.id,
                            selected: item.id === selectedQuestionId,
                            cells: [item.code, item.title, item.type, <StatusChip key="s" status={item.status} />],
                            onSelect: () => setSelectedQuestionId(item.id),
                            onEdit: () => setQuestionDialog(item),
                            onDelete: () =>
                                deactivate(t('questionnaires.deactivateConfirm', { title: item.title }), () =>
                                    deleteQuestion.mutateAsync(item.id),
                                ),
                        }))}
                    />
                )}
            </Section>

            <Section
                title={t('questionnaires.options')}
                action={
                    selectedQuestion ? (
                        <Button size="small" onClick={() => setOptionDialog('new')}>
                            {t('questionnaires.newOption')}
                        </Button>
                    ) : null
                }
            >
                {!selectedQuestion ? (
                    <Typography color="text.secondary">{t('questionnaires.selectQuestion')}</Typography>
                ) : (
                    <SimpleTable
                        empty={t('questionnaires.emptyOptions')}
                        columns={[t('questionnaires.colCode'), t('questionnaires.colLabel'), t('questionnaires.colOrder'), t('questionnaires.colStatus')]}
                        rows={options.map((item) => ({
                            id: item.id,
                            selected: false,
                            cells: [item.code, item.label, item.order, <StatusChip key="s" status={item.status} />],
                            onEdit: () => setOptionDialog(item),
                            onDelete: () =>
                                deactivate(t('questionnaires.deactivateConfirm', { title: item.label }), () =>
                                    deleteOption.mutateAsync(item.id),
                                ),
                        }))}
                    />
                )}
            </Section>

            {selected && (
                <Section title={t('questionnaires.answers')}>
                    {resultsLoading ? (
                        <Box display="flex" justifyContent="center" p={2}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('questionnaires.colUser')}</TableCell>
                                        <TableCell>{t('questionnaires.colCompleted')}</TableCell>
                                        <TableCell>{t('questionnaires.colAnswers')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {results.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={3}>{t('questionnaires.emptyAnswers')}</TableCell>
                                        </TableRow>
                                    ) : (
                                        results.map((row) => (
                                            <TableRow key={row.userId}>
                                                <TableCell>{row.name || row.email || row.userId}</TableCell>
                                                <TableCell>
                                                    {row.completed
                                                        ? t('questionnaires.completedYes')
                                                        : t('questionnaires.completedNo')}
                                                </TableCell>
                                                <TableCell>{row.answers.map(formatAnswer).join('; ')}</TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Section>
            )}

            {createOpen && <QuestionnaireDialog onClose={() => setCreateOpen(false)} />}
            {editing && <QuestionnaireDialog questionnaire={editing} onClose={() => setEditing(null)} />}
            {questionDialog && selected && (
                <QuestionDialog
                    questionnaireId={selected.id}
                    question={questionDialog === 'new' ? undefined : questionDialog}
                    onClose={() => setQuestionDialog(null)}
                />
            )}
            {optionDialog && selectedQuestion && (
                <OptionDialog
                    questionId={selectedQuestion.id}
                    option={optionDialog === 'new' ? undefined : optionDialog}
                    onClose={() => setOptionDialog(null)}
                />
            )}
        </Box>
    );
};

const StatusChip = ({ status }: { status: QuestionnaireStatus }) => (
    <Chip size="small" label={statusLabel(status)} color={status === 'active' ? 'success' : 'default'} />
);

const Section = ({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) => (
    <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle1">{title}</Typography>
            {action}
        </Box>
        {children}
    </Box>
);

const SimpleTable = ({
    columns,
    rows,
    empty,
}: {
    columns: string[];
    empty: string;
    rows: Array<{
        id: number;
        selected: boolean;
        cells: React.ReactNode[];
        onSelect?: () => void;
        onEdit: () => void;
        onDelete: () => void;
    }>;
}) => (
    <TableContainer component={Paper}>
        <Table size="small">
            <TableHead>
                <TableRow>
                    {columns.map((column) => (
                        <TableCell key={column}>{column}</TableCell>
                    ))}
                    <TableCell align="right">{t('questionnaires.colActions')}</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.length === 0 ? (
                    <TableRow>
                        <TableCell colSpan={columns.length + 1} align="center">
                            {empty}
                        </TableCell>
                    </TableRow>
                ) : (
                    rows.map((row) => (
                        <TableRow
                            key={row.id}
                            hover
                            selected={row.selected}
                            onClick={row.onSelect}
                            sx={{ cursor: row.onSelect ? 'pointer' : 'default' }}
                        >
                            {row.cells.map((cell, index) => (
                                <TableCell key={index}>{cell}</TableCell>
                            ))}
                            <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                <IconButton size="small" onClick={row.onEdit}>
                                    <EditIcon />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={row.onDelete}>
                                    <DeleteIcon />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    ))
                )}
            </TableBody>
        </Table>
    </TableContainer>
);

const StatusFields = ({
    status,
    onChange,
}: {
    status: QuestionnaireStatus;
    onChange: (status: QuestionnaireStatus) => void;
}) => (
    <RadioGroup row value={status} onChange={(e) => onChange(e.target.value as QuestionnaireStatus)}>
        <FormControlLabel value="active" control={<Radio />} label={t('questionnaires.status.active')} />
        <FormControlLabel value="inactive" control={<Radio />} label={t('questionnaires.status.inactive')} />
    </RadioGroup>
);

const QuestionnaireDialog = ({ questionnaire, onClose }: { questionnaire?: Questionnaire; onClose: () => void }) => {
    const createMutation = useCreateQuestionnaire();
    const updateMutation = useUpdateQuestionnaire();
    const [code, setCode] = useState(questionnaire?.code ?? '');
    const [title, setTitle] = useState(questionnaire?.title ?? '');
    const [description, setDescription] = useState(questionnaire?.description ?? '');
    const [order, setOrder] = useState(String(questionnaire?.order ?? 0));
    const [status, setStatus] = useState<QuestionnaireStatus>(questionnaire?.status ?? 'active');
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!title.trim() || (!questionnaire && !code.trim())) {
            alert(t('questionnaires.requiredFields'));
            return;
        }
        const dto: CreateQuestionnaireDto = {
            code: code.trim(),
            title: title.trim(),
            description: description.trim() || null,
            order: Number(order) || 0,
            status,
        };
        setSaving(true);
        try {
            if (questionnaire) {
                const { code: _code, ...rest } = dto;
                await updateMutation.mutateAsync({ id: questionnaire.id, dto: rest });
            } else {
                await createMutation.mutateAsync(dto);
            }
            onClose();
        } catch (e) {
            console.error(e);
            alert(t('questionnaires.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
            <DialogTitle>{questionnaire ? t('questionnaires.dialogEdit') : t('questionnaires.dialogNew')}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField label={t('questionnaires.colCode')} value={code} onChange={(e) => setCode(e.target.value)} size="small" disabled={!!questionnaire} />
                    <TextField label={t('questionnaires.colTitle')} value={title} onChange={(e) => setTitle(e.target.value)} size="small" />
                    <TextField label={t('questionnaires.colDescription')} value={description} onChange={(e) => setDescription(e.target.value)} size="small" multiline minRows={2} />
                    <TextField label={t('questionnaires.colOrder')} value={order} onChange={(e) => setOrder(e.target.value)} size="small" type="number" />
                    <StatusFields status={status} onChange={setStatus} />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={save} disabled={saving}>{t('common.save')}</Button>
            </DialogActions>
        </Dialog>
    );
};

const QuestionDialog = ({
    questionnaireId,
    question,
    onClose,
}: {
    questionnaireId: number;
    question?: QuestionnaireQuestion;
    onClose: () => void;
}) => {
    const createMutation = useCreateQuestion();
    const updateMutation = useUpdateQuestion();
    const [code, setCode] = useState(question?.code ?? '');
    const [title, setTitle] = useState(question?.title ?? '');
    const [description, setDescription] = useState(question?.description ?? '');
    const [type, setType] = useState<QuestionType>(question?.type ?? 'single_choice');
    const [order, setOrder] = useState(String(question?.order ?? 0));
    const [required, setRequired] = useState(question?.required ?? false);
    const [minSelections, setMinSelections] = useState(question?.minSelections == null ? '' : String(question.minSelections));
    const [maxSelections, setMaxSelections] = useState(question?.maxSelections == null ? '' : String(question.maxSelections));
    const [status, setStatus] = useState<QuestionnaireStatus>(question?.status ?? 'active');
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!title.trim() || (!question && !code.trim())) {
            alert(t('questionnaires.requiredFields'));
            return;
        }
        const dto: CreateQuestionDto = {
            code: code.trim(),
            title: title.trim(),
            description: description.trim() || null,
            type,
            order: Number(order) || 0,
            required,
            minSelections: minSelections === '' ? null : Number(minSelections),
            maxSelections: maxSelections === '' ? null : Number(maxSelections),
            status,
        };
        setSaving(true);
        try {
            if (question) {
                const { code: _code, ...rest } = dto;
                await updateMutation.mutateAsync({ id: question.id, dto: rest });
            } else {
                await createMutation.mutateAsync({ questionnaireId, dto });
            }
            onClose();
        } catch (e) {
            console.error(e);
            alert(t('questionnaires.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
            <DialogTitle>{question ? t('questionnaires.dialogEditQuestion') : t('questionnaires.dialogNewQuestion')}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField label={t('questionnaires.colCode')} value={code} onChange={(e) => setCode(e.target.value)} size="small" disabled={!!question} />
                    <TextField label={t('questionnaires.colTitle')} value={title} onChange={(e) => setTitle(e.target.value)} size="small" />
                    <TextField label={t('questionnaires.colDescription')} value={description} onChange={(e) => setDescription(e.target.value)} size="small" />
                    <TextField select label={t('questionnaires.colType')} value={type} onChange={(e) => setType(e.target.value as QuestionType)} size="small">
                        {QUESTION_TYPES.map((item) => (
                            <MenuItem key={item} value={item}>{item}</MenuItem>
                        ))}
                    </TextField>
                    <TextField label={t('questionnaires.colOrder')} value={order} onChange={(e) => setOrder(e.target.value)} size="small" type="number" />
                    <TextField label={t('questionnaires.minSelections')} value={minSelections} onChange={(e) => setMinSelections(e.target.value)} size="small" type="number" />
                    <TextField label={t('questionnaires.maxSelections')} value={maxSelections} onChange={(e) => setMaxSelections(e.target.value)} size="small" type="number" />
                    <FormControlLabel control={<Switch checked={required} onChange={(e) => setRequired(e.target.checked)} />} label={t('questionnaires.required')} />
                    <StatusFields status={status} onChange={setStatus} />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={save} disabled={saving}>{t('common.save')}</Button>
            </DialogActions>
        </Dialog>
    );
};

const OptionDialog = ({
    questionId,
    option,
    onClose,
}: {
    questionId: number;
    option?: QuestionnaireOption;
    onClose: () => void;
}) => {
    const createMutation = useCreateOption();
    const updateMutation = useUpdateOption();
    const [code, setCode] = useState(option?.code ?? '');
    const [label, setLabel] = useState(option?.label ?? '');
    const [order, setOrder] = useState(String(option?.order ?? 0));
    const [status, setStatus] = useState<QuestionnaireStatus>(option?.status ?? 'active');
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!label.trim() || (!option && !code.trim())) {
            alert(t('questionnaires.requiredFields'));
            return;
        }
        const dto: CreateOptionDto = {
            code: code.trim(),
            label: label.trim(),
            order: Number(order) || 0,
            status,
        };
        setSaving(true);
        try {
            if (option) {
                const { code: _code, ...rest } = dto;
                await updateMutation.mutateAsync({ id: option.id, dto: rest });
            } else {
                await createMutation.mutateAsync({ questionId, dto });
            }
            onClose();
        } catch (e) {
            console.error(e);
            alert(t('questionnaires.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
            <DialogTitle>{option ? t('questionnaires.dialogEditOption') : t('questionnaires.dialogNewOption')}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField label={t('questionnaires.colCode')} value={code} onChange={(e) => setCode(e.target.value)} size="small" disabled={!!option} />
                    <TextField label={t('questionnaires.colLabel')} value={label} onChange={(e) => setLabel(e.target.value)} size="small" />
                    <TextField label={t('questionnaires.colOrder')} value={order} onChange={(e) => setOrder(e.target.value)} size="small" type="number" />
                    <StatusFields status={status} onChange={setStatus} />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={save} disabled={saving}>{t('common.save')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default DashboardQuestionnaires;
